import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { useIntl } from '../../../util/reactIntl';

import { makeGetListingsByIdSelector } from '../../../ducks/marketplaceData.duck';

import { ListingCard } from '../../../components';

import css from './SimilarListings.module.css';

const EMPTY_LISTINGS = [];

const RESULT_LIMIT = 5;

// Price similarity tolerance (relative to current listing).
const PRICE_TOLERANCE = 0.3;

// Scoring weights.
//
// Garment type is DECISIVE, not merely heavy: WEIGHT_CATEGORY_LEVEL_2 is
// deliberately greater than every other weight combined (3+2+2+1+5 = 13), so
// a same-type item can never be outranked by a different-type one. Before
// this, scoring was flatly additive at weight 5, and a dress matching brand
// + size + price + condition scored 8 — beating a skirt that matched only
// the subcategory. That's why a skirt's "Lignende plagg" filled up with
// tops and dresses.
const WEIGHT_CATEGORY_LEVEL_2 = 20;
// Refines ordering WITHIN the same garment type (e.g. midi vs mini skirts).
const WEIGHT_CATEGORY_LEVEL_3 = 5;
const WEIGHT_BRAND = 3;
const WEIGHT_SIZE = 2;
const WEIGHT_PRICE = 2;
const WEIGHT_CONDITION = 1;

// The brand dropdown's catch-all. 73% of in-stock clothing carries it, so
// treating "annet" === "annet" as a brand match fired the bonus between
// completely unrelated garments. It means "unspecified", not "same brand".
const UNSPECIFIED_BRAND = 'annet';

const sizeOf = publicData =>
  publicData?.['storrelse-klar'] || publicData?.['storrelse-sko'] || null;

/**
 * Score a candidate listing against the current listing.
 *
 * Pure function of the two listing entities so it's trivial to test in
 * isolation and side-effect free for memoisation.
 */
export const scoreSimilarity = (current, candidate) => {
  const a = current?.attributes || {};
  const b = candidate?.attributes || {};
  const aPub = a.publicData || {};
  const bPub = b.publicData || {};

  let score = 0;

  if (aPub.categoryLevel2 && aPub.categoryLevel2 === bPub.categoryLevel2) {
    score += WEIGHT_CATEGORY_LEVEL_2;

    // Only meaningful inside a matching type, so it's nested rather than
    // able to pull a different-type item up.
    if (aPub.categoryLevel3 && aPub.categoryLevel3 === bPub.categoryLevel3) {
      score += WEIGHT_CATEGORY_LEVEL_3;
    }
  }

  if (aPub.merke && aPub.merke !== UNSPECIFIED_BRAND && aPub.merke === bPub.merke) {
    score += WEIGHT_BRAND;
  }

  const aSize = sizeOf(aPub);
  const bSize = sizeOf(bPub);
  if (aSize && aSize === bSize) {
    score += WEIGHT_SIZE;
  }

  const aAmount = a.price?.amount;
  const bAmount = b.price?.amount;
  if (aAmount && bAmount) {
    const ratio = Math.abs(bAmount - aAmount) / aAmount;
    if (ratio <= PRICE_TOLERANCE) {
      score += WEIGHT_PRICE;
    }
  }

  if (aPub.tilstand && aPub.tilstand === bPub.tilstand) {
    score += WEIGHT_CONDITION;
  }

  return score;
};

/**
 * "Lignende plagg" / Similar items section at the bottom of ListingPage.
 *
 * Data flow:
 *   ListingPage.duck.loadData → fetchSimilarListings thunk → API query
 *   keyed by `pub_categoryLevel1` (broadest sensible cut) → ids dropped
 *   into `state.ListingPage.similarListingIds`. This component reads
 *   that slice and reranks the candidates with `scoreSimilarity`,
 *   slicing to the top {@link RESULT_LIMIT}.
 *
 * Layout:
 *  - Mobile/tablet: horizontal carousel with two cards visible per
 *    viewport (scroll-snap so the user lands on whole cards).
 *  - Desktop: 4-column responsive grid.
 *
 * @param {Object} props
 * @param {Object} props.listing – the current listing entity
 * @returns {JSX.Element|null}
 */
const SimilarListings = props => {
  const { listing } = props;
  const intl = useIntl();

  const listingId = listing?.id?.uuid;

  const similarListingIds = useSelector(
    state => state.ListingPage?.similarListingIds || EMPTY_LISTINGS,
    shallowEqual
  );

  // Per-component memoised denormalisation selector. Without this,
  // the inline selector would build a fresh array each render and
  // react-redux v8's stability check warns about the unstable
  // reference. The factory caches against (entities, listingIds) so
  // unchanged inputs return the same array reference.
  const getListingsByIdMemoized = useMemo(makeGetListingsByIdSelector, []);
  const candidates = useSelector(
    state =>
      similarListingIds.length
        ? getListingsByIdMemoized(state, similarListingIds)
        : EMPTY_LISTINGS,
    shallowEqual
  );

  const ranked = useMemo(() => {
    const others = candidates.filter(l => l.id?.uuid !== listingId);
    const scored = others.map((l, i) => ({
      listing: l,
      score: scoreSimilarity(listing, l),
      // Preserve API order (newest first) as a stable tiebreaker.
      tiebreaker: i,
    }));
    scored.sort((a, b) => b.score - a.score || a.tiebreaker - b.tiebreaker);
    return scored.slice(0, RESULT_LIMIT).map(entry => entry.listing);
  }, [candidates, listing, listingId]);

  if (ranked.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      <h3 className={css.heading}>
        {intl.formatMessage({ id: 'ListingPage.similarListings' })}
      </h3>
      <div className={css.grid}>
        {ranked.map(other => (
          <div key={other.id.uuid} className={css.cardWrapper}>
            <ListingCard listing={other} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default SimilarListings;
